const RNS_SYNCHRONOUS_SCREEN_STATE_UPDATES_DEFAULT = true;
const RNS_SYNCHRONOUS_HEADER_CONFIG_STATE_UPDATES_DEFAULT = true;
const RNS_SYNCHRONOUS_HEADER_SUBVIEW_STATE_UPDATES_DEFAULT = true;
const RNS_ANDROID_LEGACY_TOP_INSET_BEHAVIOR_DEFAULT = false;
const RNS_ANDROID_RESET_SCREEN_SHADOW_STATE_ON_ORIENTATION_CHANGE_DEFAULT = true;
const RNS_IOS_ORIENTATION_INHERITANCE_FIX_DEFAULT = true;
const RNS_DEBUG_LOGGING = false;

// TODO: Migrate freeze here

/**
 * Exposes information useful for downstream navigation library implementers,
 * so they can keep reasonable backward compatibility, if desired.
 *
 * We don't mean for this object to only grow in number of fields, however at the same time
 * we won't be very hasty to reduce it. Expect gradual changes.
 */
export const compatibilityFlags = {
  /**
   * Because of a bug introduced in https://github.com/software-mansion/react-native-screens/pull/1646
   * react-native-screens v3.21 changed how header's backTitle handles whitespace strings in https://github.com/software-mansion/react-native-screens/pull/1726
   * To allow for backwards compatibility in @react-navigation/native-stack we need a way to check if this version or newer is used.
   * See https://github.com/react-navigation/react-navigation/pull/11423 for more context.
   */
  isNewBackTitleImplementation: true,
  /**
   * With version 4.0.0 the header implementation has been changed. To allow for backward compat
   * with native-stack@v6 we want to expose a way to check whether the new implementation
   * is in use or not.
   *
   * See:
   * * https://github.com/software-mansion/react-native-screens/pull/2325
   * * https://github.com/react-navigation/react-navigation/pull/12125
   */
  usesHeaderFlexboxImplementation: true,
  /**
   * In https://github.com/software-mansion/react-native-screens/pull/3402, we fix values
   * reported in `onHeaderHeightChange` event on Android. To allow backward compatibility in
   * `@react-navigation/native-stack`, we expose a way to check whether the new implementation
   * is in use or not.
   */
  usesNewAndroidHeaderHeightImplementation: true,
  /**
   * Numerous breaking changes in the Tabs API (experimental at the time) were
   * introduced with version 4.25.0 of the library. The API has since been
   * stabilised.
   *
   * This flag marks the shape of the stable API and enables downstream
   * to detect these changes.
   *
   * See:
   * * https://github.com/software-mansion/react-native-screens/pull/3888
   * * https://github.com/software-mansion/react-native-screens/pull/3776
   * * https://github.com/software-mansion/react-native-screens/pull/3781
   * * https://github.com/software-mansion/react-native-screens/pull/3756
   * * https://github.com/software-mansion/react-native-screens/pull/3808
   * * https://github.com/software-mansion/react-native-screens/pull/3785
   * * https://github.com/software-mansion/react-native-screens/pull/3789
   * * https://github.com/software-mansion/react-native-screens/pull/3794
   * * https://github.com/software-mansion/react-native-screens/pull/3863
   * * https://github.com/software-mansion/react-native-screens/pull/3875
   * * https://github.com/software-mansion/react-native-screens/pull/3895
   * * https://github.com/software-mansion/react-native-screens/pull/3918
   */
  usesStableTabsApi: true
};
const _featureFlags = {
  experiment: {
    synchronousScreenUpdatesEnabled: RNS_SYNCHRONOUS_SCREEN_STATE_UPDATES_DEFAULT,
    synchronousHeaderConfigUpdatesEnabled: RNS_SYNCHRONOUS_HEADER_CONFIG_STATE_UPDATES_DEFAULT,
    synchronousHeaderSubviewUpdatesEnabled: RNS_SYNCHRONOUS_HEADER_SUBVIEW_STATE_UPDATES_DEFAULT,
    androidLegacyTopInsetBehavior: RNS_ANDROID_LEGACY_TOP_INSET_BEHAVIOR_DEFAULT,
    androidResetScreenShadowStateOnOrientationChangeEnabled: RNS_ANDROID_RESET_SCREEN_SHADOW_STATE_ON_ORIENTATION_CHANGE_DEFAULT,
    iosOrientationInheritanceFixEnabled: RNS_IOS_ORIENTATION_INHERITANCE_FIX_DEFAULT
  },
  stable: {
    debugLogging: RNS_DEBUG_LOGGING
  }
};
const createExperimentalFeatureFlagAccessor = (key, defaultValue) => {
  return {
    get() {
      return _featureFlags.experiment[key];
    },
    set(value) {
      if (value !== _featureFlags.experiment[key] && _featureFlags.experiment[key] !== defaultValue) {
        console.error(`[RNScreens] ${key} feature flag modified for a second time; this might lead to unexpected effects`);
      }
      _featureFlags.experiment[key] = value;
    }
  };
};
const createStableFeatureFlagAccessor = (key, defaultValue) => {
  return {
    get() {
      return _featureFlags.stable[key];
    },
    set(value) {
      if (value !== _featureFlags.stable[key] && _featureFlags.stable[key] !== defaultValue) {
        console.error(`[RNScreens] ${key} feature flag modified for a second time; this might lead to unexpected effects`);
      }
      _featureFlags.stable[key] = value;
    }
  };
};
const synchronousScreenUpdatesAccessor = createExperimentalFeatureFlagAccessor('synchronousScreenUpdatesEnabled', RNS_SYNCHRONOUS_SCREEN_STATE_UPDATES_DEFAULT);
const synchronousHeaderConfigUpdatesAccessor = createExperimentalFeatureFlagAccessor('synchronousHeaderConfigUpdatesEnabled', RNS_SYNCHRONOUS_HEADER_CONFIG_STATE_UPDATES_DEFAULT);
const synchronousHeaderSubviewUpdatesAccessor = createExperimentalFeatureFlagAccessor('synchronousHeaderSubviewUpdatesEnabled', RNS_SYNCHRONOUS_HEADER_SUBVIEW_STATE_UPDATES_DEFAULT);
const androidLegacyTopInsetBehaviorAccessor = createExperimentalFeatureFlagAccessor('androidLegacyTopInsetBehavior', RNS_ANDROID_LEGACY_TOP_INSET_BEHAVIOR_DEFAULT);
const androidResetScreenShadowStateOnOrientationChangeAccessor = createExperimentalFeatureFlagAccessor('androidResetScreenShadowStateOnOrientationChangeEnabled', RNS_ANDROID_RESET_SCREEN_SHADOW_STATE_ON_ORIENTATION_CHANGE_DEFAULT);
const iosOrientationInheritanceFixAccessor = createExperimentalFeatureFlagAccessor('iosOrientationInheritanceFixEnabled', RNS_IOS_ORIENTATION_INHERITANCE_FIX_DEFAULT);
const rnsDebugLoggingAccessor = createStableFeatureFlagAccessor('debugLogging', RNS_DEBUG_LOGGING);

/**
 * Exposes configurable global behaviour of the library.
 *
 * Most of these can be overridden on particular component level, these are global switches.
 */
export const featureFlags = {
  /**
   *  Flags to enable experimental features. These might be removed w/o notice or moved to stable.
   */
  experiment: {
    get synchronousScreenUpdatesEnabled() {
      return synchronousScreenUpdatesAccessor.get();
    },
    set synchronousScreenUpdatesEnabled(value) {
      synchronousScreenUpdatesAccessor.set(value);
    },
    get synchronousHeaderConfigUpdatesEnabled() {
      return synchronousHeaderConfigUpdatesAccessor.get();
    },
    set synchronousHeaderConfigUpdatesEnabled(value) {
      synchronousHeaderConfigUpdatesAccessor.set(value);
    },
    get synchronousHeaderSubviewUpdatesEnabled() {
      return synchronousHeaderSubviewUpdatesAccessor.get();
    },
    set synchronousHeaderSubviewUpdatesEnabled(value) {
      synchronousHeaderSubviewUpdatesAccessor.set(value);
    },
    get androidLegacyTopInsetBehavior() {
      return androidLegacyTopInsetBehaviorAccessor.get();
    },
    set androidLegacyTopInsetBehavior(value) {
      androidLegacyTopInsetBehaviorAccessor.set(value);
    },
    get androidResetScreenShadowStateOnOrientationChangeEnabled() {
      return androidResetScreenShadowStateOnOrientationChangeAccessor.get();
    },
    set androidResetScreenShadowStateOnOrientationChangeEnabled(value) {
      androidResetScreenShadowStateOnOrientationChangeAccessor.set(value);
    },
    /**
     * Fixes legacy (Stack v4) screen orientation evaluation on iOS. On by default.
     *
     * Before this fix, a legacy `Screen` without an explicit `screenOrientation`
     * always reported `allButUpsideDown` (iPhone) / `all` (iPad), which overrode
     * the orientation set by an ancestor screen (e.g. a portrait-locked stack
     * hosting bottom tabs). With the fix enabled, such a screen defers to its
     * parent screen / the app's `Info.plist` supported orientations instead.
     *
     * PR: https://github.com/software-mansion/react-native-screens/pull/4408
     * @platform ios
     */
    get iosOrientationInheritanceFixEnabled() {
      return iosOrientationInheritanceFixAccessor.get();
    },
    set iosOrientationInheritanceFixEnabled(value) {
      iosOrientationInheritanceFixAccessor.set(value);
    },
    /**
     * Enables the fix for native / JS state desynchronization in Stack. On by default.
     * PR: https://github.com/software-mansion/react-native-screens/pull/3584
     * @deprecated This flag is no longer configurable and always returns `true`.
     */
    get iosPreventReattachmentOfDismissedScreens() {
      return true;
    },
    set iosPreventReattachmentOfDismissedScreens(_value) {},
    /**
     * Enables the fix for native / JS state desynchronization for Modals. On by default.
     * PR: https://github.com/software-mansion/react-native-screens/pull/3760
     * @deprecated This flag is no longer configurable and always returns `true`.
     */
    get iosPreventReattachmentOfDismissedModals() {
      return true;
    },
    set iosPreventReattachmentOfDismissedModals(_value) {},
    /**
     * Disables the behavior that blocks interactions during Stack Screen transition.
     * The application should immediately react to user gestures, dismissing more screens at once, etc.
     * Use only with `iosPreventReattachmentOfDismissedScreens = true` to enable the fix
     * for native / JS state desynchronization. On by default.
     * PR: https://github.com/software-mansion/react-native-screens/pull/3631
     * @deprecated This flag is no longer configurable and always returns `true`.
     */
    get ios26AllowInteractionsDuringTransition() {
      return true;
    },
    set ios26AllowInteractionsDuringTransition(_value) {}
  },
  /**
   * Section for stable flags, which can be used to configure library behaviour.
   */
  stable: {
    get debugLogging() {
      return rnsDebugLoggingAccessor.get();
    },
    set debugLogging(value) {
      rnsDebugLoggingAccessor.set(value);
    }
  }
};
export default featureFlags;
//# sourceMappingURL=flags.js.map